import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import "../adminShared/adminPage.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import { listFromResponse, isMutationSuccess } from "../../helper/helper";
import dayjs from "dayjs";

const Transmissions = () => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const load = () => {
    callApi(
      "GET",
      routes.getAllTransmissions,
      null,
      setLoading,
      (res) => {
        if (res.status === 200) setAllRows(listFromResponse(res));
      },
      () => message.error("Failed to load transmissions")
    );
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = searchText
    ? allRows.filter((r) => (r.title || "").toLowerCase().includes(searchText.toLowerCase()))
    : allRows;

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({ title: row.title });
    setModalOpen(true);
  };

  const submit = (values) => {
    setModalOpen(false);
    const current = editing;
    setEditing(null);
    form.resetFields();

    if (current) {
      callApi(
        "PATCH",
        `${routes.updateTransmission}/${current._id}`,
        { title: values.title },
        setLoading,
        (res) => {
          if (res.status === 200) {
            message.success("Transmission updated");
            load();
          }
        },
        () => message.error("Failed to update transmission")
      );
    } else {
      callApi(
        "POST",
        routes.createTransmission,
        { title: values.title },
        setLoading,
        (res) => {
          if (res.status === 200 || res.status === 201) {
            message.success("Transmission created");
            load();
          }
        },
        () => message.error("Failed to create transmission")
      );
    }
  };

  const remove = (id) => {
    callApi(
      "DELETE",
      `${routes.deleteTransmission}/${id}`,
      null,
      setLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Transmission deleted");
          load();
        }
      },
      () => message.error("Failed to delete transmission")
    );
  };

  const columns = [
    { title: "No", key: "i", width: 60, render: (_, __, i) => i + 1 },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
    },
    {
      title: "Action",
      key: "a",
      width: 160,
      render: (_, row) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this transmission?" onConfirm={() => remove(row._id)}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#555BDF" } }}>
      <div className="admin-entity-page">
        <Loader loading={loading} />
        <div className="admin-entity-header">
          <h1 className="admin-entity-title">Transmissions</h1>
        </div>
        <div className="admin-entity-card">
          <div className="admin-entity-actions">
            <h2 style={{ margin: 0, fontSize: 16 }}>Catalog</h2>
            <Space>
              <Input
                placeholder="Search…"
                prefix={<SearchOutlined />}
                allowClear
                style={{ width: 260 }}
                onChange={(e) => setSearchText(e.target.value || "")}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add transmission
              </Button>
            </Space>
          </div>
          <Table
            className="admin-entity-table"
            rowKey="_id"
            columns={columns}
            dataSource={filtered}
            scroll={{ x: true }}
          />
        </div>
        <Modal
          title={editing ? "Edit transmission" : "New transmission"}
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            setEditing(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={submit}>
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="e.g. Automatic (S Tronic)" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default Transmissions;
